// Constants
export const AGENT_NAME = 'Emma';
export const COMPANY_NAME = 'Grab';
export const CUSTOMER_NAME = 'Hairin';
export const RESTAURANT_NAME = 'The Little Cafe';
export const SCRIPTED_AGENT_ID = 'agent_grab_restaurant_onboarding';

export interface ScriptNode {
  id: string;
  type: 'greeting' | 'statement' | 'question' | 'closing' | 'decision';
  content: string;
  agentMessage: string;
  expectedResponse?: {
    positive?: string[];
    negative?: string[];
    neutral?: string[];
  };
  nextNodes?: {
    positive?: string;
    negative?: string;
    neutral?: string;
    default?: string;
  };
  isEnd?: boolean;
  backgroundColor?: string;
  requiresInput?: boolean;
  waitForResponse?: boolean;
}

export interface Script {
  id: string;
  name: string;
  description: string;
  startNode: string;
  nodes: Record<string, ScriptNode>;
  agentInfo?: {
    name: string;
    voiceId: string;
    avatar?: string;
  };
}

export const grabRestaurantOnboardingScript: Script = {
  id: 'grab-restaurant-onboarding',
  name: 'Grab Restaurant Onboarding Cold Call',
  description:
    'Scripted cold-call flow for an SDR inviting a Singapore restaurant owner to meet a Grab account executive',
  startNode: '1',
  agentInfo: {
    name: AGENT_NAME,
    voiceId: 'ljEOxtzNoGEa58anWyea',
  },
  nodes: {
    '1': {
      id: '1',
      type: 'greeting',
      content: 'Confirm the decision maker is on the line',
      agentMessage: `Hi, is this ${CUSTOMER_NAME}?`,
      backgroundColor: '#d4edda',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'speaking', 'this is hairin', 'hairin here', 'hi'],
        negative: ['no', 'wrong number', 'not hairin', "he's not here", "she's not here"],
        neutral: ['who is this', 'may i know', 'whose calling'],
      },
      nextNodes: {
        positive: '2',
        negative: '14',
        neutral: '2',
        default: '2',
      },
    },
    '2': {
      id: '2',
      type: 'statement',
      content: 'Introduce self and respect their time',
      agentMessage: `Hey ${CUSTOMER_NAME}, it's ${AGENT_NAME} from ${COMPANY_NAME}. I'll keep this really quick.`,
      backgroundColor: '#ffffff',
      requiresInput: false,
      waitForResponse: false,
      nextNodes: {
        default: '3',
      },
    },
    '3': {
      id: '3',
      type: 'statement',
      content: 'Value proposition and hook',
      agentMessage: `We help restaurants like ${RESTAURANT_NAME} pop up on ${COMPANY_NAME} the moment nearby diners open the app. Most new partners start seeing fresh orders within the first few weeks.`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['sounds good', 'interesting', 'tell me more', 'okay', 'sure'],
        negative: ['not interested', 'no', "don't need", 'stop calling'],
        neutral: ['maybe', 'hmm', 'what is this about', 'not sure'],
      },
      nextNodes: {
        positive: '4',
        negative: '8',
        neutral: '4',
        default: '4',
      },
    },
    '4': {
      id: '4',
      type: 'question',
      content: 'Quickly qualify their current delivery setup',
      agentMessage: `Can I quickly ask—are you mainly relying on walk-ins, or do you use other delivery platforms right now?`,
      backgroundColor: '#ffffcc',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['walk-ins', 'mostly walk-ins', 'walk in traffic', 'not on grab', 'not listed', 'not yet', 'new to delivery', 'delivery only'],
        neutral: ['other platforms', 'deliveroo', 'foodpanda', 'grabfood', 'now delivery', 'oddle', 'chope', 'we use delivery', 'we use now delivery'],
        negative: ['already on grab', 'we are on grab', 'listed on grab', 'yes on grab', 'on grab already'],
      },
      nextNodes: {
        positive: '5',
        neutral: '5c',
        negative: '6',
        default: '5',
      },
    },
    '5': {
      id: '5',
      type: 'statement',
      content: 'Highlight benefits for restaurants not yet on Grab',
      agentMessage: `That's exactly why I'm calling. ${COMPANY_NAME} puts your menu in front of hungry people right as they're searching, keeps them coming back with promos, and handles the delivery leg so your team can stay focused on service.`,
      backgroundColor: '#ffffff',
      requiresInput: false,
      waitForResponse: false,
      nextNodes: {
        default: '7',
      },
    },
    '6': {
      id: '6',
      type: 'statement',
      content: 'Position value for existing Grab partners',
      agentMessage: `Understood—you’re already on ${COMPANY_NAME}. I’ll note that so we don’t interrupt service again. Thanks for your time and have a smooth shift!`,
      backgroundColor: '#f8d7da',
      requiresInput: false,
      waitForResponse: false,
      isEnd: true,
    },
    '5c': {
      id: '5c',
      type: 'statement',
      content: 'Highlight benefits for competitors already using other platforms',
      agentMessage: `Totally hear you—many partners started on Deliveroo or Foodpanda. ${COMPANY_NAME} adds reach with diners who already default to us, plus sponsored listings and promos that stack on what you're doing now.`,
      backgroundColor: '#ffffff',
      requiresInput: false,
      waitForResponse: false,
      nextNodes: {
        default: '7',
      },
    },
    '7': {
      id: '7',
      type: 'question',
      content: 'Propose the account executive call',
      agentMessage: `How about a quick 20-minute call with one of our ${COMPANY_NAME} account execs? They'll show where ${RESTAURANT_NAME} can reach more diners and lift delivery sales. It's free—just a chance to see if it's a fit. Would that work for you?`,
      backgroundColor: '#d4edda',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'sure', 'okay', 'sounds good', "let's do it"],
        negative: ['no', 'not interested', 'no thanks', "don't have time"],
        neutral: ['maybe', 'not sure', 'depends'],
      },
      nextNodes: {
        positive: '11',
        negative: '8',
        neutral: '8',
        default: '8',
      },
    },
    '8': {
      id: '8',
      type: 'question',
      content: 'Diagnose the objection',
      agentMessage: `Totally fair. Is the hesitation more about carving out time, or do you feel your current delivery channels already cover you?`,
      backgroundColor: '#fff3cd',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['busy', 'no time', 'schedule', 'timing', 'swamped'],
        negative: ['other platform', 'deliveroo', 'foodpanda', 'already covered', 'have partners'],
        neutral: ['maybe later', 'not sure', 'need to think'],
      },
      nextNodes: {
        positive: '9',
        negative: '10',
        neutral: '9',
        default: '9',
      },
    },
    '9': {
      id: '9',
      type: 'question',
      content: 'Handle the “too busy” objection',
      agentMessage: `I completely get how packed service gets. The call is 20 minutes and we work around your quiet window. Would early next week before lunch or later in the afternoon feel easier?`,
      backgroundColor: '#fff3cd',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'that works', 'sure', 'okay', 'morning', 'afternoon'],
        negative: ['no', 'still busy', 'not this month', 'no time'],
        neutral: ['maybe', 'let me see', 'not sure'],
      },
      nextNodes: {
        positive: '11',
        negative: '12',
        neutral: '11',
        default: '12',
      },
    },
    '10': {
      id: '10',
      type: 'question',
      content: 'Handle “already on another platform” objection',
      agentMessage: `Of course—and a lot of restaurants we work with started that way. The reason they still choose ${COMPANY_NAME} is our reach: we're the number one app many customers open daily for food orders, so it really complements what you're already doing. Could we pencil in a short call so you can see the latest tools?`,
      backgroundColor: '#fff3cd',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'alright', 'okay', "let's do it", 'sure'],
        negative: ['no', 'still not interested', 'already covered', "don't need"],
        neutral: ['maybe', "i'll think", 'not sure'],
      },
      nextNodes: {
        positive: '11',
        negative: '12',
        neutral: '11',
        default: '12',
      },
    },
    '11': {
      id: '11',
      type: 'question',
      content: 'Lock down the meeting time',
      agentMessage: `Great. Does this week or early next week feel better? And are mornings or afternoons usually easier for you?`,
      backgroundColor: '#d4edda',
      requiresInput: true,
      waitForResponse: true,
      nextNodes: {
        default: '13',
      },
    },
    '12': {
      id: '12',
      type: 'closing',
      content: 'Respectfully close if they decline',
      agentMessage: `No worries at all, ${CUSTOMER_NAME}. Thanks for your time—I'll send over a quick summary in case you'd like to revisit later. Hope service goes smoothly at ${RESTAURANT_NAME}.`,
      backgroundColor: '#f8d7da',
      isEnd: true,
      requiresInput: false,
      waitForResponse: false,
    },
    '13': {
      id: '13',
      type: 'closing',
      content: 'Confirm follow-up and wrap up positively',
      agentMessage: `Great, I'll send you a confirmation email with the meeting details. Thanks so much, ${CUSTOMER_NAME}—looking forward to helping ${RESTAURANT_NAME} reach more customers on ${COMPANY_NAME}!`,
      backgroundColor: '#d4edda',
      isEnd: true,
      requiresInput: false,
      waitForResponse: false,
    },
    '14': {
      id: '14',
      type: 'closing',
      content: 'Close the call if the contact is unavailable',
      agentMessage: `Appreciate the heads-up. I'll note it and let ${CUSTOMER_NAME} know ${AGENT_NAME} from ${COMPANY_NAME} called. Take care.`,
      backgroundColor: '#f8d7da',
      isEnd: true,
      requiresInput: false,
      waitForResponse: false,
    },
  },
};
